import styled from "@emotion/styled";
import { DateRange } from "react-date-range";

import { fontRobotoCondensed } from "shared/lib/styles";

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  inline-size: 100%;
`;

export const FieldLabel = styled.p`
  margin: 0;

  color: #2c4692;
  font-weight: 700;
  font-size: 14px;
  font-family: ${fontRobotoCondensed};
  line-height: normal;
`;

export const Box = styled.div`
  inline-size: 100%;

  border: 1px solid #dfdfdf;
  border-radius: 12px;

  background-color: #ffffff;

  overflow: hidden;
`;

const Trigger = styled.button`
  display: flex;
  gap: 8px;
  align-items: center;

  inline-size: 100%;
  padding: 12px;

  border: none;

  cursor: pointer;
`;

export const TriggerActive = styled(Trigger)`
  background-color: #556bb4;

  &:hover {
    background-color: #556bb4;
  }
`;

export const TriggerInactive = styled(Trigger)`
  background-color: #ffffff;

  &:hover {
    background-color: #d6dced;
  }
`;

const TriggerIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;

  svg {
    inline-size: 20px;
    block-size: 20px;
  }
`;

export const TriggerIconActive = styled(TriggerIcon)`
  color: #ffffff;
`;

export const TriggerIconInactive = styled(TriggerIcon)`
  color: #788ac4;
`;

const TriggerText = styled.span`
  flex: 1 0 0;

  min-inline-size: 0;

  font-size: 14px;
  font-family: ${fontRobotoCondensed};
  line-height: normal;
  white-space: nowrap;
  text-align: start;
  text-overflow: ellipsis;

  overflow: hidden;
`;

export const TriggerTextActive = styled(TriggerText)`
  color: #ffffff;
  font-weight: 700;
`;

export const TriggerTextInactive = styled(TriggerText)`
  color: #2c4692;
  font-weight: 400;
`;

export const ClearButton = styled.span`
  display: inline-flex;
  flex-shrink: 0;

  color: #ffffff;

  cursor: pointer;

  svg {
    inline-size: 16px;
    block-size: 16px;
  }
`;

export const CalendarPanel = styled.div`
  padding-block: 4px 12px;
  padding-inline: 12px;
`;

export const PresetRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  margin-block-end: 12px;
  padding-block-end: 12px;

  border-block-end: 1px solid #dfdfdf;
`;

const PresetButton = styled.button`
  padding-block: 6px;
  padding-inline: 12px;

  font-weight: 400;
  font-size: 13px;
  font-family: ${fontRobotoCondensed};
  line-height: normal;

  border: 1px solid #dfdfdf;
  border-radius: 8px;

  cursor: pointer;
`;

export const PresetButtonActive = styled(PresetButton)`
  color: #ffffff;

  background-color: #788ac4;

  &:hover {
    background-color: #788ac4;
  }
`;

export const PresetButtonInactive = styled(PresetButton)`
  color: #000000;

  background-color: #ffffff;

  &:hover {
    background-color: #d6dced;
  }
`;

export const MonthNav = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding-block: 4px 8px;
`;

export const MonthNavButton = styled.button`
  display: inline-flex;

  padding: 0;

  color: #000000;

  border: none;

  background-color: transparent;

  cursor: pointer;

  svg {
    inline-size: 18px;
    block-size: 18px;
  }
`;

export const MonthNavLabel = styled.span`
  color: #000000;
  font-weight: 700;
  font-size: 14px;
  font-family: ${fontRobotoCondensed};
  line-height: normal;
`;

export const StyledDateRange = styled(DateRange)`
  inline-size: 100%;

  font-family: ${fontRobotoCondensed};

  ${".rdrCalendarWrapper"}, ${".rdrMonths"}, ${".rdrMonth"} {
    inline-size: 100%;
    min-inline-size: 0;
    box-sizing: border-box;
  }
  ${".rdrMonth"} {
    padding: 0;
  }
  ${".rdrDateDisplayWrapper"} {
    background-color: transparent;
  }
  ${".rdrDateDisplay"} {
    margin-block: 0 8px;
    margin-inline: 0;
  }
  ${".rdrDateInput"} {
    color: #a1a1a1;
    font-weight: 400;
    font-size: 12px;
    font-family: ${fontRobotoCondensed};
    text-align: center;

    border: none;
    border-radius: 0;
    border-block-end: 1px solid #dfdfdf;

    box-shadow: none;
  }
  ${".rdrDateDisplayItemActive"} {
    border-color: #dfdfdf;
  }
  ${".rdrWeekDay"}, ${".rdrDayNumber"} span {
    color: #222222 !important;
    font-weight: 400;
    font-size: 12px;
    font-family: ${fontRobotoCondensed};
  }
  ${".rdrDayPassive"} ${".rdrDayNumber"} span {
    color: #bbbbbb !important;
  }
  ${".rdrDayToday"} ${".rdrDayNumber"} span::after {
    display: none;
  }
  ${".rdrDayToday"} ${".rdrDayNumber"}::after {
    position: absolute;
    inset-block-start: 50%;
    inset-inline-start: 50%;
    content: "";

    inline-size: 100%;
    block-size: 26px;

    border: 1px solid #bbbbbb;
    border-radius: 24px;

    transform: translate(-50%, -50%);
  }
`;

export const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  margin-block-start: 8px;
`;

export const RightActions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

type ActionButtonProps = {
  $primary?: boolean;
};

export const ActionButton = styled.button<ActionButtonProps>`
  color: #222222;
  font-weight: ${props => (props.$primary ? 700 : 400)};
  font-size: 14px;
  font-family: ${fontRobotoCondensed};
  line-height: normal;

  border: none;

  background-color: transparent;

  cursor: pointer;
`;
